<template>
  <UtdModalDialog v-if="shouldShowModal"
                  ref="modalRef"
                  modal-classes="persistent-login-modal"
                  :close-on-overlay-click="false"
                  :close-on-escape="false"
                  :hide-header-close="true">
    <template #header>Stay signed in?</template>
    <div class="persistent-login-modal__body">
      <div class="persistent-login-modal__message">
        <p class="persistent-login-modal__text">
          <strong>Stay signed in for the next {{ persistentLoginDetails.ttlDays }} days,</strong>
          making access to UpToDate faster and easier.
          You may only do this for one device at a time.
        </p>
        <p class="persistent-login-modal__text">
          Select No if you're on a public or shared device.
        </p>
      </div>
      <utd-checkbox class="persistent-login-modal__checkbox"
                    :checked="persistentLoginDetails.doNotShowAgain"
                    @checkbox-changed="(checked) => toggleAskAgain(checked)">
        Don't ask again on this device
      </utd-checkbox>
    </div>
    <template #footer>
      <div class="persistent-login-modal__footer">
        <div class="persistent-login-modal__actions">
          <UtdButton class="persistent-login-modal__btn"
                     button-size="large"
                     button-style="ghost"
                     @click="() =>handleClick(false)">No</UtdButton>
          <UtdButton class="persistent-login-modal__btn"
                     button-size="large"
                     button-style="clean"
                     @click="() => handleClick(true)">Yes</UtdButton>
        </div>
      </div>
    </template>
  </UtdModalDialog>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import UtdCheckbox from '../shared/stdlib/UtdCheckbox.vue';
import { setCookie } from '_acaSrc/utility/Cookies';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';
import {
    SET_PERSISTENT_LOGIN_MODAL_SHOWN,
    SET_PERSISTENT_LOGIN_MODAL_PENDING,
    SET_PERSISTENT_LOGIN_MODAL_ANSWERED,
    SET_PERSISTENT_LOGIN_RESOLVE_MODAL,
    SET_PERSISTENT_LOGIN_DO_NOT_SHOW_AGAIN
} from '_acaSrc/store/auth.store';

const store = useStore();
const persistentLoginDetails = computed(() => store.getters['auth/persistentLoginDetails']);
const shouldShowModal = computed(() => persistentLoginDetails.value.modalConfig.shown);

const modalRef = ref(null);

const open = () => {
    modalRef.value.open();
};

watch(shouldShowModal, async(shown) => {
    if (shown) {
        await nextTick();
        open();
    }
});

const resolveModal = () => {
    const { resolveModal: resolve } = store.getters['auth/persistentLoginDetails'];
    if (resolve) {
        resolve();
        store.commit(`auth/${SET_PERSISTENT_LOGIN_RESOLVE_MODAL}`, null);
    }
};

const handleClick = async(shouldPersist) => {
    let userResponse;
    store.commit(`auth/${SET_PERSISTENT_LOGIN_MODAL_SHOWN}`, false);
    store.commit(`auth/${SET_PERSISTENT_LOGIN_MODAL_PENDING}`, false);
    store.commit(`auth/${SET_PERSISTENT_LOGIN_MODAL_ANSWERED}`, true);
    if (!shouldPersist) {
        const doNotShowAgain = persistentLoginDetails.value.doNotShowAgain;
        if (doNotShowAgain) {
            userResponse = 'Don\'t Ask Again';
            setCookie('_utd_plq', 'false', {expires: 183});
        }
        else {
            userResponse = 'No';
        }
    }
    else {
        userResponse = 'Yes';
        await store.dispatch('auth/setPersistentLogin');
    }
    logUiClickEventHelper({
        uiElementName: 'persistent-login-modal',
        optData: userResponse
    });
    resolveModal();
};

const toggleAskAgain = (checked) => {
    store.commit(`auth/${SET_PERSISTENT_LOGIN_DO_NOT_SHOW_AGAIN}`, checked);
};


defineExpose({ open });

</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@import (reference) '~_acaAssets/global/variables';

.persistent-login-modal {
  .utd-modal-content {
    max-width: 500px;

    @media (max-width: 761px) {
      .ds1-ml-2();
      .ds1-mr-2();
      max-width: none;
    }
  }

  .utd-dialog-content {
    .ds1-pa-2();
    overflow-y: visible;
    max-height: none;
    background-color: @WKCE-WHITE;
  }

  .persistent-login-modal__body {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .persistent-login-modal__message {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .persistent-login-modal__text {
    .ds1-utd-size-3();
    .ds1-mt-0();
    .ds1-mb-0();
  }

  .persistent-login-modal__footer {
    .ds1-pl-2();
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }

  .persistent-login-modal__actions {
    display: flex;
    justify-content: flex-end;

    @media (max-width: 350px) {
      flex-direction: column-reverse;
      width: 100%;
      gap: 12px;

      .utd-button {
        .ds1-ml-0();
        width: auto;
      }
    }
  }

  .persistent-login-modal__btn {
    @media (min-width: 351px) {
      width: 86px;
    }
  }
}
</style>